import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  ArrowRight,
  RotateCcw,
  ThumbsUp,
  ThumbsDown,
  GlassWater,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { track } from '@/lib/analytics/tracking';
import { useAuth } from '@/features/auth/useAuth';
import { usePublicInventoryItems } from '@/features/tasting/useTastingItems';
import { getProductPhotoPublicUrl } from '@/features/inventory/productPhotoStorage';
import {
  sendFeedback,
  useSendChat,
  type ChatAudience,
  type ChatMessage,
  type ChatStreamOptions,
} from './useChatbot';

// Sections de permission → libellé FR, pour dire à l'IA staff ce que
// l'employé connecté peut ouvrir (elle n'oriente que vers ça).
const SECTION_LABELS: Record<string, string> = {
  schedule: 'Horaire',
  shifts: 'Quarts',
  inventory: 'Inventaire',
  hr: 'Ressources humaines',
  recipes: 'Recettes',
  directory: 'Répertoire',
  keys: 'Clés',
  formation: 'Formation',
  'payroll-tips': 'Pourboires',
  'payroll-salary': 'Entrée de temps',
  'payroll-report': 'Rapport de paie',
  'prix-vente': 'Prix de vente',
  reservations: 'Réservations',
  events: 'Événements',
  settings: 'Paramètres',
};

// Persistence : sessionStorage (autorisé dans le projet, contrairement
// à localStorage qui est banni). Clé séparée par audience pour ne pas
// mélanger les conversations public/staff. La conversation persiste
// jusqu'à la fermeture de l'onglet.
const STORAGE_KEY_PREFIX = 'baralambic-chat-';
const MAX_PERSISTED_MESSAGES = 30; // garde-fou anti-bloat sessionStorage

const MAX_INPUT = 2000;

// Greeting plain-text gardé pour fallback / persistence sessionStorage.
// Le rendu visuel utilise un <WelcomeCard> plus joli quand c'est le seul
// message dans la conversation (voir la condition messages.length === 1).
const GREETINGS: Record<ChatAudience, string> = {
  public:
    "Salut ! Comment puis-je t'aider ? Je peux te suggérer des cocktails ou whiskys selon tes goûts, te parler de notre menu et de la bouffe, t'aider à réserver une table, ou répondre à tes questions sur les événements à venir.",
  staff:
    "Salut ! Je suis l'assistant interne. Je t'aide à naviguer dans l'app de gestion — demande-moi où trouver une feature ou comment faire une action.",
};

const WELCOME_TITLE: Record<ChatAudience, string> = {
  public: "Bienvenue au Bar L'Alambic",
  staff: 'Salut chef !',
};

const WELCOME_INTRO: Record<ChatAudience, string> = {
  public:
    "Pose-moi tes questions sur nos **cocktails**, **whiskys**, **horaires** ou **réservations**. Je te réponds en direct.",
  staff:
    "Je t'aide à trouver une fonction dans l'app, ou à comprendre comment faire une action. Demande, ou utilise une des suggestions.",
};

const HEADER_LABELS: Record<ChatAudience, { title: string; subtitle: string }> = {
  public: { title: "Assistant Bar L'Alambic", subtitle: 'Réponses immédiates · IA' },
  staff: { title: 'Assistant interne', subtitle: 'Aide à utiliser le système · IA' },
};

// Quick replies : suggestions de questions affichées au démarrage de
// la conversation (juste après le greeting, avant que l'user tape quoi
// que ce soit). Click = envoi automatique du message comme si tapé.
const QUICK_REPLIES: Record<ChatAudience, string[]> = {
  public: [
    'Vos horaires ?',
    'Faire une réservation',
    'Voir les cocktails',
    'Prochains événements',
  ],
  staff: [
    'Voir mon horaire',
    'Donner mes disponibilités',
    'Échanger un quart',
    'Signaler un bris',
  ],
};

interface Props {
  /** 'public' (default) = visiteurs du site. 'staff' = employés connectés. */
  audience?: ChatAudience;
}

/**
 * Widget de chat flottant en bas à droite. Bulle dorée fermée, fenêtre
 * de conversation ouverte. État local seulement — pas de persistence
 * entre reloads (par design, MVP).
 *
 * Le `audience` détermine :
 * - Le system prompt côté serveur (public vs staff)
 * - Le greeting initial
 * - Le label du header
 */
export default function ChatWidget({ audience = 'public' }: Props) {
  const initialGreeting: ChatMessage = {
    role: 'assistant',
    content: GREETINGS[audience],
  };
  const headerLabel = HEADER_LABELS[audience];
  const storageKey = `${STORAGE_KEY_PREFIX}${audience}`;

  const [isOpen, setIsOpen] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  // Restore conversation depuis sessionStorage (lazy initial state).
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    if (typeof window === 'undefined') return [initialGreeting];
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as ChatMessage[];
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {
      // sessionStorage indispo ou JSON corrompu → fallback
    }
    return [initialGreeting];
  });
  const [input, setInput] = useState('');
  const send = useSendChat(audience);

  // Identité de l'employé connecté — envoyée seulement pour l'audience
  // 'staff', pour personnaliser et brancher ses vraies données (RLS).
  const { employee, profile, session, hasSectionAccess } = useAuth();
  const staffExtras = useMemo<ChatStreamOptions | undefined>(() => {
    if (audience !== 'staff') return undefined;
    const accessibleSections = Object.keys(SECTION_LABELS)
      .filter((id) => hasSectionAccess(id))
      .map((id) => SECTION_LABELS[id]);
    return {
      token: session?.access_token ?? null,
      userContext: {
        firstName: employee?.first_name ?? null,
        role: profile?.role ?? null,
        accessibleSections,
      },
    };
  }, [
    audience,
    employee?.first_name,
    profile?.role,
    session?.access_token,
    hasSectionAccess,
  ]);

  // Persiste la conversation à chaque changement. Skip si vide ou
  // juste le greeting (pas la peine de persister un état neuf).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (messages.length <= 1) return;
    try {
      const toStore = messages.slice(-MAX_PERSISTED_MESSAGES);
      sessionStorage.setItem(storageKey, JSON.stringify(toStore));
    } catch {
      // Quota dépassé ou désactivé — silencieux
    }
  }, [messages, storageKey]);

  const handleReset = () => {
    setMessages([initialGreeting]);
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Scroll automatique vers le dernier message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, send.isPending]);

  // Focus input à l'ouverture
  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => inputRef.current?.focus(), 250);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Escape pour fermer
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen]);

  // Track quand le chatbot s'ouvre (peu importe l'origine : clic,
  // deep-link). Le effect ne fire QU'AU passage isOpen=true (pas au close).
  useEffect(() => {
    if (isOpen) track('chatbot_opened', { audience });
  }, [isOpen, audience]);

  // `directText` permet aux quick replies de send sans passer par l'input.
  // Streaming SSE : on append un message assistant VIDE, puis on remplit
  // au fur et à mesure que les chunks arrivent. Le typing indicator reste
  // visible tant qu'aucun delta n'est encore arrivé.
  const handleSend = async (directText?: string) => {
    if (send.isPending) return;
    const text = (directText ?? input).trim();
    if (!text) return;

    const userMsg: ChatMessage = { role: 'user', content: text };
    const nextMessages = [...messages, userMsg];
    // Ajoute aussi un assistant vide pour qu'on puisse appender les deltas.
    const placeholderIdx = nextMessages.length;
    setMessages([...nextMessages, { role: 'assistant', content: '' }]);
    if (!directText) setInput('');

    track('chatbot_message_sent', {
      audience,
      message_index: messages.length,
    });

    try {
      await send.sendStream(
        nextMessages.slice(-20),
        (delta) => {
          setMessages((prev) => {
            const copy = [...prev];
            const target = copy[placeholderIdx];
            if (target && target.role === 'assistant') {
              copy[placeholderIdx] = {
                ...target,
                content: target.content + delta,
              };
            }
            return copy;
          });
        },
        staffExtras,
      );
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Erreur inconnue. Réessaie.';
      setMessages((prev) => {
        const copy = [...prev];
        copy[placeholderIdx] = {
          role: 'assistant',
          content: `Désolé, j'ai eu un pépin : ${errorMsg}`,
        };
        return copy;
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter envoie, Shift+Enter saute une ligne
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  return (
    <>
      {/* Bouton bulle flottant + ripple doré subtil pour attirer l'œil */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 380, damping: 25 }}
            className="fixed bottom-6 right-6 z-50"
          >
            {/* Ripple : 2 anneaux dorés qui s'élargissent + fade. Off si
                l'user a prefers-reduced-motion. Subtil (opacité max 0.35). */}
            {!prefersReducedMotion && (
              <>
                <motion.span
                  aria-hidden="true"
                  className="absolute inset-0 rounded-full bg-[#D4A574] pointer-events-none"
                  animate={{ scale: [1, 1.6], opacity: [0.35, 0] }}
                  transition={{
                    duration: 2.4,
                    repeat: Infinity,
                    ease: 'easeOut',
                  }}
                />
                <motion.span
                  aria-hidden="true"
                  className="absolute inset-0 rounded-full bg-[#D4A574] pointer-events-none"
                  animate={{ scale: [1, 1.6], opacity: [0.35, 0] }}
                  transition={{
                    duration: 2.4,
                    repeat: Infinity,
                    ease: 'easeOut',
                    delay: 1.2,
                  }}
                />
              </>
            )}
            <button
              type="button"
              onClick={() => setIsOpen(true)}
              aria-label="Ouvrir le chat avec l'assistant du bar"
              className="relative h-14 w-14 rounded-full bg-[#D4A574] text-[#0A0A0A] shadow-2xl shadow-[#D4A574]/40 hover:bg-[#C9A36B] hover:scale-105 active:scale-95 transition-all flex items-center justify-center"
            >
              <MessageCircle className="h-6 w-6" strokeWidth={2.2} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fenêtre de chat */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 380, damping: 28 }}
            className={cn(
              'fixed z-50 flex flex-col',
              // Mobile : full-screen sauf un peu de marge
              'inset-2 sm:inset-auto sm:bottom-6 sm:right-6',
              'sm:h-[600px] sm:w-[400px] sm:max-h-[80vh]',
              'rounded-2xl border border-[#1F1F1F] bg-[#0A0A0A] shadow-2xl shadow-black/60',
              'overflow-hidden',
            )}
            role="dialog"
            aria-label="Chat avec l'assistant du Bar L'Alambic"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#1F1F1F] bg-gradient-to-r from-[#D4A574]/15 via-[#D4A574]/5 to-transparent">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="h-9 w-9 rounded-full bg-[#D4A574] text-[#0A0A0A] flex items-center justify-center shrink-0">
                  <MessageCircle className="h-4.5 w-4.5" strokeWidth={2.2} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#E8E0D6] truncate">
                    {headerLabel.title}
                  </p>
                  <p className="text-[10px] text-neutral-400 truncate">
                    {headerLabel.subtitle}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                {messages.length > 1 && (
                  <button
                    type="button"
                    onClick={handleReset}
                    aria-label="Nouvelle conversation"
                    title="Nouvelle conversation"
                    className="h-8 w-8 rounded-md text-neutral-400 hover:text-[#D4A574] hover:bg-[#1A1A1A] inline-flex items-center justify-center"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  aria-label="Fermer le chat"
                  className="h-8 w-8 rounded-md text-neutral-400 hover:text-[#D4A574] hover:bg-[#1A1A1A] inline-flex items-center justify-center"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
            >
              {/* État vierge (juste le greeting) : afficher la WelcomeCard
                  custom plutôt qu'une bulle assistante basique + chips
                  séparés. Beaucoup plus engageant pour la première
                  impression. */}
              {messages.length === 1 && !send.isPending ? (
                <WelcomeCard
                  audience={audience}
                  onQuickReply={(q) => void handleSend(q)}
                />
              ) : (
                <>
                  {messages.map((msg, i) => {
                    // Le message user précédent — passé au MessageBubble pour
                    // que le bouton feedback ait du contexte à envoyer.
                    const prevUserMsg =
                      msg.role === 'assistant' && i > 0
                        ? messages[i - 1]?.role === 'user'
                          ? messages[i - 1].content
                          : ''
                        : '';
                    // Pas de feedback sur le greeting initial (index 0).
                    const showFeedback =
                      msg.role === 'assistant' &&
                      i > 0 &&
                      msg.content.length > 0 &&
                      !send.isPending;
                    return (
                      <MessageBubble
                        key={i}
                        message={msg}
                        onNavigate={() => setIsOpen(false)}
                        showFeedback={showFeedback}
                        audience={audience}
                        userMessageContext={prevUserMsg}
                      />
                    );
                  })}
                  {send.isPending &&
                    (() => {
                      const last = messages[messages.length - 1];
                      // Cache le typing si le dernier message stream déjà
                      // (a du contenu) — la bulle se remplit en direct.
                      const stillWaitingFirstChunk =
                        !last ||
                        last.role !== 'assistant' ||
                        last.content === '';
                      return stillWaitingFirstChunk ? <TypingIndicator /> : null;
                    })()}
                </>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-[#1F1F1F] p-3 bg-[#0F0F0F]">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) =>
                    setInput(e.target.value.slice(0, MAX_INPUT))
                  }
                  onKeyDown={handleKeyDown}
                  placeholder="Pose ta question…"
                  disabled={send.isPending}
                  rows={1}
                  maxLength={MAX_INPUT}
                  className="flex-1 resize-none bg-[#141414] border border-[#2A2A2A] rounded-lg px-3 py-2 text-sm text-[#E8E0D6] placeholder:text-neutral-500 focus:outline-none focus:border-[#D4A574]/50 focus-visible:ring-1 focus-visible:ring-[#D4A574]/30 disabled:opacity-50 max-h-32"
                />
                <button
                  type="button"
                  onClick={() => void handleSend()}
                  disabled={!input.trim() || send.isPending}
                  aria-label="Envoyer le message"
                  className="h-10 w-10 shrink-0 rounded-lg bg-[#D4A574] text-[#0A0A0A] hover:bg-[#C9A36B] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center transition-colors"
                >
                  {send.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </button>
              </div>
              <p className="text-[9px] text-neutral-500 mt-1.5 text-center">
                Powered by Claude · les réponses peuvent contenir des erreurs
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Rend un message texte avec :
// - Markdown gras `**texte**` → <strong>
// - URLs http(s) et www → <a> cliquable target=_blank
// - Téléphones format québécois (873) 476-0136 → tel:
// Simple regex-based, pas de lib markdown pour garder le bundle léger.
function renderRichText(text: string, linkClass: string): React.ReactNode {
  // 1. Pattern combiné : bold | url | phone
  // On split en préservant les matches via capture groups.
  const pattern =
    /(\*\*[^*]+\*\*)|(https?:\/\/[^\s)<>]+|www\.[^\s)<>]+)|(\(\d{3}\)\s?\d{3}-\d{4})/g;

  const parts = text.split(pattern).filter((p) => p !== undefined && p !== '');

  return parts.map((part, i) => {
    // Bold
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    // URL
    if (/^(https?:\/\/|www\.)/.test(part)) {
      const href = part.startsWith('www.') ? `https://${part}` : part;
      return (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
        >
          {part}
        </a>
      );
    }
    // Téléphone
    if (/^\(\d{3}\)\s?\d{3}-\d{4}$/.test(part)) {
      const digits = part.replace(/\D/g, '');
      return (
        <a key={i} href={`tel:+1${digits}`} className={linkClass}>
          {part}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

// ─────────────────────────────────────────────────────────────
// Actions CTA — boutons cliquables sous les messages de l'assistant.
// Détecte les URLs et téléphones dans la réponse et les transforme en
// boutons avec un label parlant (« Réserver une table », « Voir le
// menu », « Appeler », etc.).
// ─────────────────────────────────────────────────────────────

interface ChatAction {
  type: 'url' | 'tel' | 'internal';
  href: string;
  label: string;
}

const URL_LABELS: Record<string, string> = {
  // Pages publiques (vitrine)
  '/reserver': 'Réserver une table ou la salle',
  '/reserver/table': 'Réserver une table',
  '/menu': 'Voir le menu',
  '/cocktails': 'Voir les cocktails',
  '/vins': 'Voir les vins',
  '/degustation': 'Voir la dégustation',
  '/evenements': 'Voir les événements',
  '/a-propos': 'À propos du bar',
  '/mon-compte': 'Mon compte',
  '/compte/inscription': 'Créer mon compte',
  '/compte/connexion': 'Me connecter',
  // Pages staff (dashboard)
  '/shifts': 'Ouvrir le rapport du jour',
  '/shifts/historique': 'Voir l\'historique des quarts',
  '/admin/reservations': 'Gérer les réservations',
  '/admin/reservations/plan': 'Éditer le plan d\'étage',
  '/admin/events': 'Gérer les événements',
  '/admin/customers': 'Voir les comptes clients',
  '/inventory': 'Ouvrir l\'inventaire',
  '/payroll': 'Voir les paies',
  '/payroll/salary': 'Entrée de temps',
  '/employees': 'Voir l\'équipe',
  '/settings': 'Ouvrir les paramètres',
  '/settings/opening-hours': 'Modifier les horaires d\'ouverture',
  '/mon-profil': 'Mon profil',
  // Pages employé (self-service)
  '/horaire': 'Voir l\'horaire',
  '/horaire/mon-horaire': 'Voir mon horaire',
  '/horaire/mes-disponibilites': 'Mes disponibilités',
  '/ma-formation': 'Ma formation',
  '/mes-documents': 'Mes documents',
  '/recettes': 'Voir les recettes',
  '/recettes/bouffe': 'Recettes bouffe',
  '/repertoire': 'Répertoire de l\'équipe',
  '/keys': 'Suivi des clés',
  '/aide': 'Guide d\'utilisation',
};

function deriveUrlLabel(href: string): string {
  // Path interne (commence par /)
  if (href.startsWith('/')) {
    const path = href.replace(/\/+$/, '').toLowerCase() || '/';
    return URL_LABELS[path] ?? `Aller sur ${path}`;
  }
  // URL absolue
  try {
    const u = new URL(href);
    const path = u.pathname.replace(/\/+$/, '').toLowerCase() || '/';
    if (URL_LABELS[path]) return URL_LABELS[path];
    return `Ouvrir ${u.hostname}`;
  } catch {
    return 'Ouvrir le lien';
  }
}

// Liste des paths internes connus — pour le regex de détection. On évite
// les faux positifs (ex: une date "12/05/2026") en limitant aux préfixes
// qui existent vraiment dans le router.
const INTERNAL_PATH_PREFIXES = [
  'shifts',
  'admin',
  'inventory',
  'payroll',
  'employees',
  'cocktails',
  'settings',
  'mon-profil',
  'mon-compte',
  'menu',
  'reserver',
  'evenements',
  'vins',
  'degustation',
  'a-propos',
  'compte',
  // Pages employé (self-service) — sinon les liens ne deviennent pas des boutons
  'horaire',
  'ma-formation',
  'formation',
  'mes-documents',
  'recettes',
  'repertoire',
  'keys',
  'aide',
];

function extractActions(text: string): ChatAction[] {
  const actions: ChatAction[] = [];
  const seen = new Set<string>();

  // 1. URLs absolues (http(s) ou www)
  const urlRegex = /https?:\/\/[^\s)<>]+|www\.[^\s)<>]+/g;
  let match: RegExpExecArray | null;
  while ((match = urlRegex.exec(text)) !== null) {
    const url = match[0].replace(/[.,;:!?]+$/, '');
    const href = url.startsWith('www.') ? `https://${url}` : url;
    if (!seen.has(href)) {
      seen.add(href);
      actions.push({ type: 'url', href, label: deriveUrlLabel(href) });
    }
  }

  // 2. Paths internes (/admin/..., /shifts, etc.) — préfixes whitelistés
  // pour éviter de matcher des trucs comme "12/05/2026".
  const internalPattern = new RegExp(
    `(?<![/\\w])\\/(?:${INTERNAL_PATH_PREFIXES.join('|')})(?:\\/[a-zA-Z0-9-]+)*`,
    'g',
  );
  while ((match = internalPattern.exec(text)) !== null) {
    const path = match[0].replace(/[.,;:!?]+$/, '');
    if (!seen.has(path)) {
      seen.add(path);
      actions.push({ type: 'internal', href: path, label: deriveUrlLabel(path) });
    }
  }

  // 3. Téléphones format (XXX) XXX-XXXX
  const phoneRegex = /\(\d{3}\)\s?\d{3}-\d{4}/g;
  while ((match = phoneRegex.exec(text)) !== null) {
    const digits = match[0].replace(/\D/g, '');
    const href = `tel:+1${digits}`;
    if (!seen.has(href)) {
      seen.add(href);
      actions.push({ type: 'tel', href, label: `Appeler ${match[0]}` });
    }
  }

  return actions;
}

function MessageBubble({
  message,
  onNavigate,
  showFeedback = false,
  audience,
  userMessageContext,
}: {
  message: ChatMessage;
  onNavigate: () => void;
  /** Affiche les boutons 👍👎 sous la bulle assistante. */
  showFeedback?: boolean;
  audience?: ChatAudience;
  /** Dernier message user (envoyé au backend pour contexte du vote). */
  userMessageContext?: string;
}) {
  const isUser = message.role === 'user';
  const navigate = useNavigate();
  const linkClass = isUser
    ? 'underline underline-offset-2 font-medium hover:opacity-80'
    : 'underline underline-offset-2 text-[#D4A574] hover:text-[#E8C49A]';

  const actions = !isUser ? extractActions(message.content) : [];

  // Extrait les IDs de produits suggérés (URLs /degustation?item=UUID).
  // Ces items s'afficheront en cards riches au lieu de boutons génériques.
  const suggestedItemIds = useMemo(() => {
    if (isUser) return [];
    const ids = new Set<string>();
    // Match aussi bien l'URL absolue que le path relatif
    const re = /\/degustation\?item=([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/gi;
    let m: RegExpExecArray | null;
    while ((m = re.exec(message.content)) !== null) {
      ids.add(m[1]);
    }
    return [...ids];
  }, [message.content, isUser]);

  // Filtre les actions pour retirer les /degustation?item= déjà affichés
  // en card riche (évite le doublon "card" + "bouton" pour le même item).
  const filteredActions = actions.filter((a) => {
    if (suggestedItemIds.length === 0) return true;
    return !/\/degustation\?item=/i.test(a.href);
  });

  // État local du feedback : null = pas voté, 'up' / 'down' = voté.
  // Non persisté (re-render au refresh = reset, par design MVP).
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);

  const handleActionClick = (action: ChatAction) => {
    if (action.type === 'internal') {
      // Navigation SPA via React Router (pas de full reload)
      navigate(action.href);
      onNavigate();
    }
    // url et tel laissent le browser gérer via href
  };

  const handleFeedback = (rating: 'up' | 'down') => {
    if (feedback || !audience || !userMessageContext) return;
    setFeedback(rating); // optimistic UI
    void sendFeedback({
      audience,
      userMessage: userMessageContext,
      assistantMessage: message.content,
      rating,
    });
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-2',
        isUser ? 'items-end' : 'items-start',
      )}
    >
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words',
          isUser
            ? 'bg-[#D4A574] text-[#0A0A0A] rounded-br-sm'
            : 'bg-[#141414] border border-[#1F1F1F] text-[#E8E0D6] rounded-bl-sm',
        )}
      >
        {renderRichText(message.content, linkClass)}
      </div>

      {/* Cards produits suggérés (URLs /degustation?item=UUID) */}
      {suggestedItemIds.length > 0 && (
        <div className="flex flex-col gap-2 max-w-[85%] w-full">
          {suggestedItemIds.map((id) => (
            <ChatProductCard
              key={id}
              itemId={id}
              onNavigate={() => {
                navigate(`/degustation?item=${id}`);
                onNavigate();
              }}
            />
          ))}
        </div>
      )}

      {filteredActions.length > 0 && (
        <div className="flex flex-wrap gap-2 max-w-[85%]">
          {filteredActions.map((a, i) =>
            a.type === 'internal' ? (
              <button
                key={i}
                type="button"
                onClick={() => handleActionClick(a)}
                className="group inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#D4A574] text-[#0A0A0A] hover:bg-[#C9A36B] text-xs font-semibold shadow-md shadow-[#D4A574]/20 transition-all hover:shadow-lg hover:shadow-[#D4A574]/30 active:scale-95"
              >
                {a.label}
                <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </button>
            ) : (
              <a
                key={i}
                href={a.href}
                {...(a.type === 'url'
                  ? { target: '_blank', rel: 'noopener noreferrer' }
                  : {})}
                className="group inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#D4A574] text-[#0A0A0A] hover:bg-[#C9A36B] text-xs font-semibold shadow-md shadow-[#D4A574]/20 transition-all hover:shadow-lg hover:shadow-[#D4A574]/30 active:scale-95"
              >
                {a.label}
                <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </a>
            ),
          )}
        </div>
      )}

      {/* Feedback 👍👎 : 2 boutons discrets sous chaque réponse assistant.
          Quand l'user vote, on cache l'autre et on dore le sélectionné. */}
      {showFeedback && (
        <div className="flex items-center gap-1 text-neutral-500 mt-0.5">
          {feedback === null ? (
            <>
              <button
                type="button"
                onClick={() => handleFeedback('up')}
                aria-label="Bonne réponse"
                title="Bonne réponse"
                className="h-6 w-6 rounded hover:bg-[#1A1A1A] hover:text-[#D4A574] inline-flex items-center justify-center transition-colors"
              >
                <ThumbsUp className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={() => handleFeedback('down')}
                aria-label="Mauvaise réponse"
                title="Mauvaise réponse"
                className="h-6 w-6 rounded hover:bg-[#1A1A1A] hover:text-red-400 inline-flex items-center justify-center transition-colors"
              >
                <ThumbsDown className="h-3 w-3" />
              </button>
            </>
          ) : (
            <span
              className={cn(
                'inline-flex items-center gap-1 text-[10px]',
                feedback === 'up' ? 'text-[#D4A574]' : 'text-red-400',
              )}
            >
              {feedback === 'up' ? (
                <ThumbsUp className="h-3 w-3" />
              ) : (
                <ThumbsDown className="h-3 w-3" />
              )}
              Merci pour ton feedback !
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ChatProductCard : card riche pour un produit suggéré par l'IA.
// Affichée inline dans la bulle assistant quand le message contient
// une URL /degustation?item=UUID. Clic = navigation vers la fiche.
// ─────────────────────────────────────────────────────────────
function ChatProductCard({
  itemId,
  onNavigate,
}: {
  itemId: string;
  onNavigate: () => void;
}) {
  const { data: allItems = [], isLoading } = usePublicInventoryItems();
  const item = allItems.find((it) => it.id === itemId) ?? null;

  if (isLoading) {
    return (
      <div className="rounded-xl border border-[#1F1F1F] bg-[#0F0F0F] p-3 text-xs text-neutral-500 flex items-center gap-2">
        <Loader2 className="h-3 w-3 animate-spin text-[#D4A574]" />
        Chargement du produit…
      </div>
    );
  }

  if (!item) {
    // Item supprimé ou non public — on cache la card silencieusement
    return null;
  }

  const photoUrl = getProductPhotoPublicUrl(item.image_url, {
    width: 200,
    quality: 75,
  });
  const isWine = item.public_type === 'wine';
  const wineBottle =
    isWine && item.wine_bottle_price != null && item.wine_bottle_price > 0
      ? item.wine_bottle_price
      : null;
  const wineGlass =
    isWine && item.wine_glass_price != null && item.wine_glass_price > 0
      ? item.wine_glass_price
      : null;
  const hasWinePrice = wineBottle != null || wineGlass != null;
  const perOzPrice =
    !isWine && item.sale_mode === 'a_l_oz' && item.sale_price_per_oz
      ? item.sale_price_per_oz
      : null;
  const perUnitPrice =
    !isWine && item.sale_mode === 'par_unite' && item.sale_price_per_unit
      ? item.sale_price_per_unit
      : null;

  return (
    <button
      type="button"
      onClick={onNavigate}
      className="group w-full text-left rounded-xl border border-[#1F1F1F] bg-[#141414] hover:border-[#D4A574]/50 hover:bg-[#1A1A1A] transition-all overflow-hidden flex items-stretch gap-3 p-2"
    >
      {/* Photo */}
      <div className="shrink-0 w-16 h-16 rounded-md bg-[#0A0A0A] overflow-hidden">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={item.name}
            loading="lazy"
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <GlassWater className="h-5 w-5 text-neutral-700" />
          </div>
        )}
      </div>
      {/* Infos */}
      <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5">
        <div className="text-sm font-medium text-[#E8E0D6] truncate group-hover:text-[#D4A574] transition-colors">
          {item.name}
        </div>
        <div className="text-[10px] text-neutral-500 truncate">
          {item.category_label}
        </div>
        {/* Prix */}
        <div className="flex items-baseline gap-2 mt-0.5">
          {isWine ? (
            hasWinePrice ? (
              <>
                {wineBottle != null && (
                  <span className="text-[11px] font-display text-[#D4A574] tabular-nums">
                    {wineBottle.toFixed(2)} $ <span className="text-[9px] text-neutral-500">btl</span>
                  </span>
                )}
                {wineGlass != null && (
                  <span className="text-[11px] font-display text-[#D4A574]/80 tabular-nums">
                    {wineGlass.toFixed(2)} $ <span className="text-[9px] text-neutral-500">verre</span>
                  </span>
                )}
              </>
            ) : (
              <span className="text-[10px] italic text-neutral-500">Prix à venir</span>
            )
          ) : perOzPrice != null ? (
            <span className="text-[11px] font-display text-[#D4A574] tabular-nums">
              {perOzPrice.toFixed(2)} $ <span className="text-[9px] text-neutral-500">/oz</span>
            </span>
          ) : perUnitPrice != null ? (
            <span className="text-[11px] font-display text-[#D4A574] tabular-nums">
              {perUnitPrice.toFixed(2)} $
            </span>
          ) : null}
        </div>
      </div>
      {/* Chevron */}
      <ArrowRight className="h-4 w-4 self-center text-neutral-500 group-hover:text-[#D4A574] group-hover:translate-x-0.5 transition-all shrink-0" />
    </button>
  );
}

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="bg-[#141414] border border-[#1F1F1F] rounded-2xl rounded-bl-sm px-4 py-3 inline-flex items-center gap-1">
        <span className="h-1.5 w-1.5 rounded-full bg-[#D4A574] animate-bounce [animation-delay:-0.3s]" />
        <span className="h-1.5 w-1.5 rounded-full bg-[#D4A574] animate-bounce [animation-delay:-0.15s]" />
        <span className="h-1.5 w-1.5 rounded-full bg-[#D4A574] animate-bounce" />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// WelcomeCard : carte d'accueil rendue à la place du greeting bulle
// quand la conversation est vierge. Plus chaleureuse qu'une bulle
// texte, intègre les quick replies directement.
// ─────────────────────────────────────────────────────────────
function WelcomeCard({
  audience,
  onQuickReply,
}: {
  audience: ChatAudience;
  onQuickReply: (text: string) => void;
}) {
  // Rend le markdown gras (**...**) en <strong> sans pulling une lib.
  const renderIntro = (text: string) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
      p.startsWith('**') && p.endsWith('**') ? (
        <strong key={i} className="text-[#D4A574]">
          {p.slice(2, -2)}
        </strong>
      ) : (
        <span key={i}>{p}</span>
      ),
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-2xl border border-[#D4A574]/25 bg-gradient-to-br from-[#D4A574]/8 via-[#141414] to-[#0F0F0F] p-5 space-y-4"
    >
      {/* Logo du bar (audience public) ou icone bulle (audience staff) */}
      <div className="flex items-center justify-center">
        <div className="relative">
          {audience === 'public' ? (
            <img
              src="/images/bar_lalambic.png"
              alt="Logo Bar L'Alambic"
              className="h-16 w-16 rounded-full object-cover shadow-lg shadow-[#D4A574]/30"
              draggable={false}
            />
          ) : (
            <div className="h-14 w-14 rounded-full bg-[#D4A574] text-[#0A0A0A] flex items-center justify-center shadow-lg shadow-[#D4A574]/30">
              <MessageCircle className="h-7 w-7" strokeWidth={2.2} />
            </div>
          )}
          {/* Ripple subtil derrière l'avatar */}
          <motion.span
            aria-hidden="true"
            className="absolute inset-0 rounded-full bg-[#D4A574] pointer-events-none -z-10"
            animate={{ scale: [1, 1.4], opacity: [0.35, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Titre + intro */}
      <div className="text-center space-y-2">
        <h3 className="font-display text-xl text-[#E8E0D6] leading-tight">
          {WELCOME_TITLE[audience]}
        </h3>
        <p className="text-xs text-neutral-300 leading-relaxed px-2">
          {renderIntro(WELCOME_INTRO[audience])}
        </p>
      </div>

      {/* Divider subtil */}
      <div className="flex items-center gap-2">
        <div className="h-px flex-1 bg-[#D4A574]/15" />
        <span className="text-[9px] uppercase tracking-[0.2em] text-[#D4A574]/60">
          Suggestions
        </span>
        <div className="h-px flex-1 bg-[#D4A574]/15" />
      </div>

      {/* Quick replies en grille 2 colonnes */}
      <div className="grid grid-cols-2 gap-2">
        {QUICK_REPLIES[audience].map((q) => (
          <button
            key={q}
            type="button"
            onClick={() => onQuickReply(q)}
            className="px-3 py-2 rounded-lg bg-[#0A0A0A] border border-[#D4A574]/25 hover:border-[#D4A574]/60 hover:bg-[#D4A574]/10 text-xs text-[#E8E0D6] hover:text-[#D4A574] transition-all active:scale-95 text-left"
          >
            {q}
          </button>
        ))}
      </div>
    </motion.div>
  );
}
